import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Panel, TokenLogo, VerifiedBadge, StarButton } from "../components/common/Panel";
import { CopyBtn, Row } from "../components/common/Detail";
import { Loading, NotFound } from "./BlockDetail";
import { getToken, getTokenHolders } from "../lib/api";
import { shorten, fmtNum, fmtEth } from "../lib/format";
import { useWatchlist } from "../hooks/useWatchlist";

const PAGE_SIZE = 25;

export default function TokenDetail() {
  const { addr } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("holders");
  const { isStarred, toggle } = useWatchlist();

  // holders pagination + search
  const [holders, setHolders] = useState(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [hLoading, setHLoading] = useState(false);

  useEffect(() => {
    setData(null); setErr(false); setPage(1); setQ(""); setSearchInput("");
    getToken(addr).then(setData).catch(() => setErr(true));
    const id = setInterval(() => getToken(addr).then(setData).catch(() => {}), 8000);
    return () => clearInterval(id);
  }, [addr]);

  useEffect(() => {
    if (tab !== "holders") return;
    setHLoading(true);
    getTokenHolders(addr, { page, limit: PAGE_SIZE, q })
      .then(setHolders)
      .catch(() => setHolders({ holders: [], total: 0, pages: 1, page: 1 }))
      .finally(() => setHLoading(false));
  }, [addr, tab, page, q]);

  const doSearch = (e) => { e.preventDefault(); setPage(1); setQ(searchInput.trim()); };

  if (err) return <NotFound label="Token" navigate={navigate} />;
  if (!data) return <Loading />;

  const pages = holders?.pages || 1;

  return (
    <div className="fade-in" data-testid="token-detail-page">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="nav-link" data-testid="back-btn"><i className="fa-solid fa-arrow-left" /></button>
        <TokenLogo logo={data.logo} symbol={data.symbol} size={40} />
        <div className="flex-1">
          <span className="eyebrow" style={{ color: "var(--amber)" }}>BCC-20 Token</span>
          <h1 className="font-display text-[24px] font-extrabold mt-1 flex items-center gap-2 flex-wrap">
            {data.name} <span className="pill pill-amber align-middle">{data.symbol}</span>
            {data.verified && <VerifiedBadge />}
          </h1>
        </div>
        <StarButton starred={isStarred(data.address)} onToggle={() => toggle(data.address)} size={16} testId="star-token-detail" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <div className="panel p-4"><div className="eyebrow mb-1">Total Supply</div><div className="stat-value text-[20px]" style={{ color: "var(--amber)" }}>{fmtNum(data.totalSupply)}</div></div>
        <div className="panel p-4"><div className="eyebrow mb-1">Holders</div><div className="stat-value text-[20px]">{fmtNum(data.holderCount)}</div></div>
        <div className="panel p-4"><div className="eyebrow mb-1">Transfers</div><div className="stat-value text-[20px]">{fmtNum(data.transferCount)}</div></div>
        <div className="panel p-4"><div className="eyebrow mb-1">Decimals</div><div className="stat-value text-[20px]">{data.decimals}</div></div>
      </div>

      <Panel title="Token Contract" className="mb-5">
        <Row label="Contract Address" mono>{data.address}<CopyBtn text={data.address} testId="copy-token-address" /></Row>
        {data.deployTx && <Row label="Deploy Tx" mono><Link to={`/tx/${data.deployTx}`} className="link-amber">{data.deployTx}</Link></Row>}
      </Panel>

      <div className="flex gap-2 mb-3">
        <button className={`nav-link ${tab === "holders" ? "active" : ""}`} onClick={() => setTab("holders")} data-testid="tab-holders">Holders ({data.holderCount})</button>
        <button className={`nav-link ${tab === "transfers" ? "active" : ""}`} onClick={() => setTab("transfers")} data-testid="tab-transfers">Transfers ({data.transferCount})</button>
        {data.source && <button className={`nav-link ${tab === "code" ? "active" : ""}`} onClick={() => setTab("code")} data-testid="tab-code"><i className="fa-solid fa-circle-check mr-1" style={{ color: "var(--green)" }} />Source Code</button>}
      </div>

      {tab === "code" ? (
        <Panel title="Verified Source Code" subtitle={data.contractName || ""} action={<VerifiedBadge />} testId="panel-token-source">
          <div className="p-4">
            {data.verifiedBy && <div className="text-[11.5px] mb-2" style={{ color: "var(--muted)" }}>Verified by <span className="mono">{data.verifiedBy}</span></div>}
            <pre className="ta-dark overflow-auto text-[12px] mono p-3 rounded-lg" style={{ maxHeight: 460, whiteSpace: "pre" }} data-testid="token-source-viewer">{data.source}</pre>
          </div>
        </Panel>
      ) : tab === "holders" ? (
        <Panel
          title="Holders"
          subtitle={holders ? `${fmtNum(holders.total)} matched` : ""}
          action={
            <form onSubmit={doSearch} className="flex items-center gap-2">
              <input className="input-dark text-[12px] py-1.5" style={{ width: 200 }} value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)} placeholder="Search address 0x…" data-testid="holder-search-input" />
              <button type="submit" className="btn-amber py-1.5 px-3 text-[12px]" data-testid="holder-search-btn">Search</button>
            </form>
          }
          bodyClass="overflow-auto"
          testId="panel-holders"
        >
          <table className="dt">
            <thead><tr><th>#</th><th>Address</th><th>Balance</th><th>%</th></tr></thead>
            <tbody>
              {(holders?.holders || []).map((h, i) => (
                <tr key={h.address} onClick={() => (window.location.href = `/address/${h.address}`)} data-testid={`holder-row-${i}`}>
                  <td className="mono" style={{ color: "var(--muted)" }}>{h.rank}</td>
                  <td><Link to={`/address/${h.address}`} className="link-amber mono text-[12px]" onClick={(e) => e.stopPropagation()}>{h.address}</Link></td>
                  <td className="mono">{fmtNum(Math.round(h.balanceFmt))} {data.symbol}</td>
                  <td className="mono" style={{ color: "var(--muted)" }}>{(h.pct ?? 0).toFixed(2)}%</td>
                </tr>
              ))}
              {hLoading && <tr><td colSpan={4} className="text-center py-8" style={{ color: "var(--muted)" }}>Loading…</td></tr>}
              {!hLoading && (holders?.holders || []).length === 0 && <tr><td colSpan={4} className="text-center py-8" style={{ color: "var(--muted)" }}>{q ? "No holders match your search." : "No holders"}</td></tr>}
            </tbody>
          </table>
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button className="nav-link" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="holders-prev"><i className="fa-solid fa-chevron-left mr-1" /> Prev</button>
              <span className="mono text-[12px]" style={{ color: "var(--muted)" }} data-testid="holders-page-indicator">Page {holders?.page || page} of {pages}</span>
              <button className="nav-link" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} data-testid="holders-next">Next <i className="fa-solid fa-chevron-right ml-1" /></button>
            </div>
          )}
        </Panel>
      ) : (
        <Panel title="Transfer Events" bodyClass="overflow-auto" testId="panel-transfers">
          <table className="dt">
            <thead><tr><th>Tx</th><th>Block</th><th>From</th><th>To</th><th>Amount</th></tr></thead>
            <tbody>
              {data.transfers.map((t, i) => (
                <tr key={i} onClick={() => (window.location.href = `/tx/${t.txHash}`)} data-testid={`transfer-row-${i}`}>
                  <td><Link to={`/tx/${t.txHash}`} className="link-amber mono text-[12px]" onClick={(e) => e.stopPropagation()}>{shorten(t.txHash, 8, 6)}</Link></td>
                  <td><Link to={`/block/${t.block}`} className="link-amber mono" onClick={(e) => e.stopPropagation()}>#{t.block}</Link></td>
                  <td className="mono text-[12px]" style={{ color: "var(--muted)" }}>{shorten(t.from, 6, 4)}</td>
                  <td className="mono text-[12px]" style={{ color: "var(--muted)" }}>{shorten(t.to, 6, 4)}</td>
                  <td className="mono">{fmtNum(Math.round(t.valueFmt))} {data.symbol}</td>
                </tr>
              ))}
              {data.transfers.length === 0 && <tr><td colSpan={5} className="text-center py-8" style={{ color: "var(--muted)" }}>No transfers</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
